import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { AppBody } from "../AppBody";
import Breadcrumbs from "../../containers/Breadcrumbs";
import {
  appHeaderTitle,
  removeUrlParameter,
  sortObjValue,
  getCookiebyName,
  sodVariableDefinitionData,
} from "../../common/services/Utilities";
import HelpButton from "../HelpContent/HelpButton";
import makeStyles from "@mui/styles/makeStyles";
import BackButton from "../../containers/BackButton";
import { KeyValue, Query, SimpleTableRow, TableHeader } from "../../interfaces";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { apiPost, fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl, AppUrl } from "../../config";
import MoreOptionsMenu from "./MoreOptionsMenu";
import Edit from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import LoadingIndicator from "../LoadingIndicator";
import { useHistory, useLocation, Link as RouterLink } from "react-router-dom";
import PeerGroupCriteriaTable from "./PeerGroupCriteriaTable";
import ModalDialog from "../ModalDialog";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import { Buffer } from "buffer";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

const useStyles = makeStyles(theme => ({
  button: {
    alignItems: "center",
    fontSize: "1rem",
    padding: 0,
    fontWeight: 400,
    margin: 0,
    width: 280,
    height: 40,
  },
  loadingSpinner: { textAlign: "center", marginTop: "2rem" },
  divider: {
    backgroundColor: "#1a4480",
    marginTop: ".5rem",
  },
  margin: {
    marginTop: "1rem",
    marginBottom: "1rem",
  },
  deleteDialogContent: {
    color: theme.palette.primary.main,
  },
  deleteDialog: {
    "textAlign": "center",
    "& button": {
      height: 40,
      width: 150,
      fontSize: "1rem",
    },
    "& div#delete-dialog-title h2": {
      fontSize: "1.75rem",
    },
  },
  list: {
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: 4,
      color: theme.palette.primary.main,
    },
  },
  popperButtons: {
    "display": "block",
    "marginTop": 0,
    "width": "100%",
    "textAlign": "left",
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  modifyButton: {
    "wordBreak": "break-word",
    "padding": ".25rem",
    "margin": ".25rem 0",
    "borderRadius": 5,
    "float": "right",
    "&:hover, &.Mui-focusVisible": {
      borderRadius: 50,
    },
  },
  savedListName: {
    "& > span": {
      color: theme.palette.primary.main,
      fontSize: "1rem",
    },
  },
  footnote: { marginTop: 16 },
}));

interface Props {
  appState: Query;
  setSavedReportId: (id: string) => void;
  updateTab: (tab: string) => void;
  banner: string;
  updateUi: (name: string, value: any) => void;
}

interface Headers {
  title: string;
  apiField: string;
}

export default function ManageSavedPeerGroups({
  appState,
  setSavedReportId,
  updateTab,
  banner,
  updateUi,
}: Props) {
  const classes = useStyles();
  const user = getCookiebyName("id_token");
  const headerMetaData: Headers[] = [
    { title: "Line Item Description", apiField: "label" },
    { title: "Variable", apiField: "variable_ytd" },
  ];
  const sodDownloadConfigHeaderMetaData: Headers[] = [
    { title: "Line Item Description", apiField: "label" },
    { title: "Structure Scope", apiField: "structure_scope" },
    { title: "Variable", apiField: "variable_ytd" },
  ];
  const reportHeaders: TableHeader[][] = [
    headerMetaData.map(element => ({
      title: element.title,
      styles: { textAlign: "left" },
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];
  const sodDownloadConfigHeader: TableHeader[][] = [
    sodDownloadConfigHeaderMetaData.map(element => ({
      title: element.title,
      styles: { textAlign: "left" },
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];
  const transformDataToTableBodyData = (
    tableData: KeyValue[],
    sod?: boolean,
  ) => {
    return tableData.map((data: KeyValue) => {
      const tableCellData: SimpleTableRow = {};
      (sod ? sodDownloadConfigHeaderMetaData : headerMetaData)
        .map((value: Headers) => value.apiField)
        .forEach((apiField: string) => {
          tableCellData[apiField] = {
            value: data[apiField],
            styles: { verticalAlign: "top", textAlign: "left" },
          };
        });
      return tableCellData;
    });
  };
  const history = useHistory();
  const location = useLocation();
  const [locationState] = React.useState(location);
  const [backButtonState] = React.useState(
    history.location?.state?.url?.includes("SOD")
      ? "SOD"
      : history.location?.history?.includes("FR")
      ? "FR"
      : "PGC",
  );
  const [savedGroups, setSavedGroups] = React.useState<KeyValue[]>([]);
  const [fetchGroups, setFetchGroups] = React.useState(true);
  const [savedReports, setSavedReports] = React.useState<KeyValue[]>([]);
  const [fetchReports, setFetchReports] = React.useState(true);
  const [fetchDownloads, setFetchDownloads] = React.useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState({
    open: false,
    value: "",
    endpoint: "",
  });
  const [groupsLoading, setGroupsLoading] = React.useState(false);
  const [
    sodDownloadConfigsLoading,
    setSodDownloadConfigsLoading,
  ] = React.useState(false);
  const [displayReportLoading, setDisplayReportLoading] = React.useState(false);
  const [itemId, setItemId] = React.useState<string>("");
  const [tempRenameValue, setTempRenameValue] = React.useState<string>("");
  const [selectedPeerGroups, setSelectedPeerGroups] = React.useState<
    KeyValue[]
  >([]);
  const [savedSODDownloadConfigs, setSavedSODDownloadConfigs] = React.useState<
    KeyValue[]
  >([]);
  const [openGroupModal, setOpenGroupModal] = React.useState(false);
  const [openReportModal, setOpenReportModal] = React.useState(false);
  const [openSODDownloadModal, setOpenSODDownloadModal] = React.useState(false);
  const [currentGroup, setCurrentGroup] = React.useState({
    filters: [{}] as KeyValue[],
    title: "",
    custom: false,
    appState: undefined,
    isMulti: false,
  });
  const [currentReport, setCurrentReport] = React.useState({
    structureData: [] as KeyValue[],
    title: "",
    custom: false,
  });
  const [currentSODDownload, setCurrentSODDownload] = React.useState<KeyValue>(
    {},
  );
  const [activeTab, setActiveTab] = React.useState(
    user && appState.ui.tab
      ? appState.ui.tab
      : user && backButtonState === "SOD"
      ? "Custom SOD Downloads"
      : user && backButtonState === "FR"
      ? "Custom Financial Reports"
      : user && backButtonState === "PGC"
      ? "Custom Peer Groups"
      : "",
  );

  function openGroupTable(index: number) {
    setOpenGroupModal(true);
    setCurrentGroup({
      filters: selectedPeerGroups[index].filter_criteria,
      title: selectedPeerGroups[index].name,
      custom: selectedPeerGroups[index].is_custom,
      isMulti: selectedPeerGroups[index].isMultiCert,
      appState: selectedPeerGroups[index].saved_state
        ? JSON.parse(
            Buffer.from(
              selectedPeerGroups[index].saved_state,
              "base64",
            ).toString("utf-8"),
          )
        : undefined,
    });
  }
  function openReportTable(index: number) {
    setOpenReportModal(true);
    setCurrentReport({
      structureData: transformDataToTableBodyData(
        savedReports[index].structure[0].fields,
      ),
      title: savedReports[index].title,
      custom: savedReports[index].is_custom,
    });
  }
  function openSODDownloadConfigTable(index: number) {
    setOpenSODDownloadModal(true);

    const variables = savedSODDownloadConfigs[index].saved_state.variable[0];
    const location = savedSODDownloadConfigs[index].saved_state.location;
    let result: KeyValue[] = [];

    sodVariableDefinitionData.forEach((data: KeyValue) => {
      data.children.forEach((child: KeyValue) => {
        if (variables.includes(child.variable)) {
          result.push({
            variable_ytd: child.variable,
            label: child.title,
            structure_scope: child.hasOwnProperty("SODType")
              ? child.SODType
              : "",
          });
        }
      });
    });
    setCurrentSODDownload({
      data: transformDataToTableBodyData(result, true),
      title: savedSODDownloadConfigs[index].name,
      locations: location,
    });
  }
  function editGroupsButton(value: KeyValue) {
    if (value.isMultiCert) {
      history.push({
        pathname: `${AppUrl("PeerGroup")}/definegroup`,
        search: `createGroupType=cert&savedGroupId=${value.ID}`,
      });
    } else {
      history.push({
        pathname: `${AppUrl("PeerGroup")}/definegroup`,
        search: `createGroupType=search&savedGroupId=${value.ID}`,
      });
    }
  }
  function editReportButton(value: KeyValue) {
    setSavedReportId(value.ID);
    history.push({
      pathname: `${AppUrl("PeerGroup")}/definereport`,
    });
  }
  function editDownloadConfigs(value: KeyValue) {
    history.push({
      history: "peergroup",
      pathname: `${AppUrl("SOD")}/customDownload`,
      name: `${value.name}`,
      state: {
        url: AppUrl("SOD") + window.location.search,
      },
    });
  }
  const reportTableBody = (
    <SimpleTableBody tableData={currentReport.structureData} />
  );

  const sodDownloadTableBody = (
    <SimpleTableBody tableData={currentSODDownload.data} />
  );

  const [closeDialog, setCloseDialog] = React.useState(false);
  const contentType =
    activeTab === "Custom SOD Downloads"
      ? "SOD Download Config"
      : activeTab === "Custom Financial Reports"
      ? "Custom Report"
      : activeTab.slice(0, -1);
  const handleDeleteClose = () =>
    setOpenDeleteDialog({ open: false, value: "", endpoint: "" });
  const deleteDialog = (
    <Dialog
      id="delete-dialog"
      onClose={handleDeleteClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      open={openDeleteDialog.open}
      className={classes.deleteDialog}
    >
      <DialogContent className={classes.deleteDialogContent}>
        <DialogTitle id="delete-dialog-title">
          Delete Saved {contentType}?
        </DialogTitle>
        <DialogContentText
          variant="body2"
          color="primary"
          id="delete-dialog-description"
        >
          You are about to delete this Saved {contentType}. This cannot be
          undone. Once deleted you will have to recreate this {contentType} from
          scratch.
        </DialogContentText>
        <DialogTitle id="delete-dialog-subtitle">
          Do you wish to continue?
        </DialogTitle>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <Button
              onClick={handleDeleteClose}
              color="secondary"
              variant="contained"
              id="cancel-delete"
            >
              Cancel
            </Button>
          </Grid>
          <Grid item sm={6} xs={12}>
            <Button
              variant="contained"
              onClick={() => {
                deleteReportOrGroup(
                  openDeleteDialog.value,
                  openDeleteDialog.endpoint,
                );
                handleDeleteClose();
              }}
              color="primary"
              id="continue-delete"
            >
              Continue
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reloadReports = urlParams.get("reloadReports") ?? "";
    const reloadGroups = urlParams.get("reloadGroups") ?? "";
    const reloadConfigs = locationState?.reload ?? "";
    if (reloadReports) {
      history.push(removeUrlParameter(window.location.href, ["reloadReports"]));
      setDisplayReportLoading(true);
      const timer = setTimeout(() => {
        setFetchReports(true);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (reloadGroups) {
      history.push(removeUrlParameter(window.location.href, ["reloadGroups"]));
      setGroupsLoading(true);
      const timer = setTimeout(() => {
        setFetchGroups(true);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (reloadConfigs) {
      history.push(removeUrlParameter(window.location.href, ["reloadConfigs"]));
      setSodDownloadConfigsLoading(true);
      const timer = setTimeout(() => {
        setFetchDownloads(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [history, locationState]);

  React.useEffect(() => {
    let isMounted = true;
    setFetchGroups(false);
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/peer_groups/my_groups`,
        apiPost(
          { filters: "is_custom:true", limit: 10000, offset: 0 },
          { Authorization: `Bearer ${user}` },
        ),
      )
        .then(response => {
          if (isMounted && user && fetchGroups) {
            setCloseDialog(false);
            setSavedGroups(sortObjValue(response.data, "name"));
            setSelectedPeerGroups(response.data);
          }
        })
        .catch(error => {
          console.error(`${error.name} : ${error.message}`);
        })
        .finally(() => {
          isMounted = false;
          const timer = setTimeout(() => {
            setGroupsLoading(false);
          }, 200);
          return () => {
            clearTimeout(timer);
          };
        });
    })();
  }, [fetchGroups, groupsLoading, user]);

  React.useEffect(() => {
    let isMounted = true;
    setFetchDownloads(false);
    setSodDownloadConfigsLoading(true);
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/custom_sod/my_custom_sod`,
        {
          limit: 10000,
          offset: 0,
        },
        {
          Authorization: `Bearer ${getCookiebyName("id_token")}`,
        },
      )
        .then(response => {
          if (isMounted && user && fetchDownloads) {
            setCloseDialog(false);
            setSavedSODDownloadConfigs(sortObjValue(response.data, "name"));
          }
        })
        .catch(error => {
          console.error(`${error.name} : ${error.message}`);
        })
        .finally(() => {
          isMounted = false;
          const timer = setTimeout(() => {
            setSodDownloadConfigsLoading(false);
          }, 200);
          return () => {
            clearTimeout(timer);
          };
        });
    })();
  }, [user, activeTab, fetchDownloads]);

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted && user && fetchReports) {
      setFetchReports(false);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/report_types/my_reports`,
          apiPost(
            { filters: "is_custom:true", limit: 10000, offset: 0 },
            { Authorization: `Bearer ${user}` },
          ),
        )
          .then(response => {
            setSavedReports(sortObjValue(response.data, "title"));
            updateUi(
              "savedReportNames",
              response.data.map((report: any) => report.title),
            );
            setCloseDialog(false);
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            const timer = setTimeout(() => {
              setDisplayReportLoading(false);
            }, 200);
            return () => {
              isMounted = false;
              clearTimeout(timer);
            };
          });
      })();
    }
  }, [
    displayReportLoading,
    fetchGroups,
    fetchReports,
    groupsLoading,
    updateUi,
    user,
  ]);
  const deleteReportOrGroup = (id: string, endpoint: string) => {
    const deleteID = {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${user}`,
        "Content-Type": "application/json",
        "x-auth": "bfs",
        "x-rate-limit": "off",
      },
    };
    fetch(`${ApiBaseUrl()}/${endpoint}/${id}`, deleteID).then(() => {
      endpoint === "peer_groups"
        ? setFetchGroups(true)
        : endpoint === "report_types"
        ? setFetchReports(true)
        : setFetchDownloads(true);
    });
  };

  const postFetch = (endpoint: string, post: KeyValue) => {
    fetch(`${ApiBaseUrl()}/${endpoint}`, post).then(() => {
      endpoint === "peer_groups"
        ? setFetchGroups(true)
        : endpoint === "report_types"
        ? setFetchReports(true)
        : setFetchDownloads(true);
    });
  };
  const renamePost = (value: KeyValue, endpoint: string, newName: string) => {
    const name = {
      [endpoint === "peer_groups" || endpoint === "custom_sod"
        ? "name"
        : "title"]: newName,
    };
    const renamePostReq = apiPost(
      { ...value, ...name, ID: value.ID },
      { "x-auth": "bfs", "Authorization": `Bearer ${user}` },
    );
    postFetch(endpoint, renamePostReq);
  };
  const duplicatePost = (value: KeyValue, endpoint: string) => {
    setCloseDialog(true);
    const groupLookup: KeyValue = {
      report_types: savedReports.map(report => report.title),
      peer_groups: savedGroups.map(group => group.name),
      custom_sod: savedSODDownloadConfigs.map(config => config.name),
    };
    const nameKey =
      endpoint === "peer_groups" || endpoint === "custom_sod"
        ? "name"
        : "title";
    let newName = `${value[nameKey]}_copy`;
    while (groupLookup[endpoint].includes(newName)) {
      newName = `${newName}_copy`;
    }
    const duplicatePostReq = apiPost(
      {
        ...value,
        [nameKey]: newName,
        ID: `${value.created_by}_${Date.now()}`,
      },
      { "x-auth": "bfs", "Authorization": `Bearer ${user}` },
    );
    postFetch(endpoint, duplicatePostReq);
  };
  const editSubmit = (
    newName: string,
    value: KeyValue,
    group?: boolean,
    type?: string,
  ) => {
    const editName = group || type ? value.name : value.title;
    if (newName === editName || !newName) {
      setItemId("");
      setTempRenameValue("");
    } else {
      renamePost(
        value,
        group ? "peer_groups" : type ? "custom_sod" : "report_types",
        newName,
      );
      setItemId("");
      setTempRenameValue("");
    }
  };
  const cancelEdit = () => {
    setItemId("");
    setTempRenameValue("");
  };

  const regexTest = (e: React.ChangeEvent<HTMLInputElement>) =>
    !/[&+#,]/g.test(e.target.value);
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (regexTest(event)) {
      setTempRenameValue(event.target.value);
    }
  };

  const itemListButtons = (
    value: KeyValue,
    endpoint: string,
    index: number,
  ) => {
    const itemName =
      endpoint === "peer_groups" || endpoint === "custom_sod"
        ? value.name
        : value.title;
    const type =
      endpoint === "peer_groups"
        ? "group"
        : endpoint === "report_types"
        ? "report"
        : "custom_sod";
    const openModal = () => {
      return endpoint === "peer_groups"
        ? openGroupTable(index)
        : endpoint === "report_types"
        ? openReportTable(index)
        : openSODDownloadConfigTable(index);
    };
    const editButtonOnClick = () => {
      return endpoint === "peer_groups"
        ? editGroupsButton(value)
        : endpoint === "report_types"
        ? editReportButton(value)
        : editDownloadConfigs(value);
    };
    return (
      <ListItem className={classes.list}>
        <Grid item md={10}>
          <ListItemText primary={itemName} className={classes.savedListName} />
        </Grid>
        <Grid item md={2}>
          <MoreOptionsMenu
            id={`${type}-option-${index}`}
            popperPosition="left-start"
            toolTipText={`More Options ${
              value.title ? value.title : value.name
            }`}
            icon={<MoreHorizIcon />}
            close={closeDialog}
          >
            <Button
              id="view-item"
              className={classes.popperButtons}
              onClick={openModal}
            >
              {endpoint === "peer_groups"
                ? "View Peers & Criteria"
                : endpoint === "report_types"
                ? "View Selected Variables"
                : "Preview SOD Download Config"}
            </Button>
            <Button
              id="rename-item"
              className={classes.popperButtons}
              onClick={() => {
                setItemId(value.ID);
                setTempRenameValue(itemName);
              }}
            >
              {endpoint === "peer_groups"
                ? "Rename Peer Group"
                : endpoint === "report_types"
                ? "Rename Custom Report"
                : "Rename SOD Download Config"}
            </Button>
            <Button
              id="duplicate-item"
              className={classes.popperButtons}
              onClick={() => duplicatePost(value, endpoint)}
            >
              {endpoint === "peer_groups"
                ? "Duplicate Peer Group"
                : endpoint === "report_types"
                ? "Duplicate Custom Report"
                : "Duplicate SOD Download Config"}
            </Button>
            <Button
              id="delete-item"
              className={classes.popperButtons}
              onClick={() => {
                setOpenDeleteDialog({
                  open: true,
                  value: value.ID,
                  endpoint,
                });
              }}
            >
              Delete Item From List
            </Button>
          </MoreOptionsMenu>
        </Grid>
        <Grid item md={2}>
          <IconButton
            className={classes.modifyButton}
            title={`Edit ${
              endpoint === "peer_groups"
                ? "Group"
                : endpoint === "report_types"
                ? "Report"
                : "SOD Download Config"
            }`}
            id={
              endpoint === "peer_groups"
                ? `edit-group-${index}`
                : endpoint === "report_types"
                ? `edit-report-${index}`
                : `edit-sod-download-config-${index}`
            }
            onClick={editButtonOnClick}
            size="large"
          >
            <Edit color="primary" />
          </IconButton>
        </Grid>
      </ListItem>
    );
  };

  const duplicateNameCheck = (value: string) => {
    const groupLookup: KeyValue = {
      "Custom Financial Reports": savedReports.map(report => report.title),
      "Custom Peer Groups": savedGroups.map(group => group.name),
      "Custom SOD Downloads": savedSODDownloadConfigs.map(
        config => config.name,
      ),
    };
    return groupLookup[activeTab]
      .reduce(
        (acc: string[], name: string) =>
          name !== value ? [...acc, name] : acc,
        [],
      )
      .includes(tempRenameValue);
  };

  const editForm = (
    value: KeyValue,
    index: number,
    group?: boolean,
    type?: string,
  ) => {
    const nameIsDuplicate = duplicateNameCheck(
      activeTab === "Custom Financial Reports" ? value.title : value.name,
    );
    return (
      <form
        key={index}
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          editSubmit(tempRenameValue, value, group, type);
        }}
      >
        <FormControl fullWidth>
          <TextField
            fullWidth
            id="modifyTextField"
            label={`Rename ${group ? "Peer Group" : contentType}`}
            variant="outlined"
            value={tempRenameValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleNameChange(e)
            }
            error={nameIsDuplicate}
            aria-errormessage="wow"
            InputProps={{
              endAdornment: (
                <>
                  <InputAdornment position="end">
                    <IconButton
                      id="cancelEdit"
                      title="Cancel Edit"
                      onClick={cancelEdit}
                      size="large"
                    >
                      <ClearIcon color="primary" />
                    </IconButton>
                  </InputAdornment>
                  <InputAdornment position="end">
                    <IconButton
                      id="saveEdit"
                      type="submit"
                      title="Save Edit"
                      size="large"
                      disabled={nameIsDuplicate}
                      color="primary"
                    >
                      <CheckIcon />
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
          />
          {nameIsDuplicate && (
            <Alert
              id="certs-required"
              severity="error"
              style={{ padding: "0 .5rem" }}
            >
              Name already in use.
            </Alert>
          )}
        </FormControl>
      </form>
    );
  };
  const SavedPeerGroupsList = () => {
    return (
      <Grid container style={{ display: "block" }}>
        {groupsLoading ? (
          <LoadingIndicator
            spinnerClass={classes.loadingSpinner}
            textClass={classes.loadingSpinner}
            spinnerSize={50}
            text="Loading"
          />
        ) : (
          <List>
            {savedGroups.map((value: KeyValue, index) => {
              const labelId = `group-${index}`;

              return (
                <div key={labelId} id={labelId}>
                  {itemId === value.ID
                    ? editForm(value, index, true)
                    : itemListButtons(value, "peer_groups", index)}
                </div>
              );
            })}
          </List>
        )}
      </Grid>
    );
  };

  const SavedSODDownloadConfigs = () => {
    return (
      <Grid container style={{ display: "block" }}>
        {sodDownloadConfigsLoading ? (
          <LoadingIndicator
            spinnerClass={classes.loadingSpinner}
            textClass={classes.loadingSpinner}
            spinnerSize={50}
            text="Loading"
          />
        ) : (
          <List>
            {savedSODDownloadConfigs.map((value: KeyValue, index) => {
              const labelId = `config-${index}`;
              return (
                <div key={labelId} id={labelId}>
                  {itemId === value.ID
                    ? editForm(value, index, false, "custom_sod")
                    : itemListButtons(value, "custom_sod", index)}
                </div>
              );
            })}
          </List>
        )}
      </Grid>
    );
  };

  const SavedReportsList = () => {
    return (
      <Grid container style={{ display: "block" }}>
        {displayReportLoading ? (
          <LoadingIndicator
            spinnerClass={classes.loadingSpinner}
            textClass={classes.loadingSpinner}
            spinnerSize={50}
            text="Loading"
          />
        ) : (
          <List>
            {savedReports.map((value: KeyValue, index) => {
              const labelId = `report-${index}`;

              return (
                <div key={labelId} id={labelId}>
                  {itemId === value.ID
                    ? editForm(value, index, false)
                    : itemListButtons(value, "report_types", index)}
                </div>
              );
            })}
          </List>
        )}
      </Grid>
    );
  };
  useInitialPageLoad("peerGroup");
  const button = (label: string, className: string, onClick: () => void) => {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        className={classes.margin}
      >
        <Button
          className={className}
          id={label.toLowerCase().replace(/ /g, "-")}
          variant="contained"
          color={"primary"}
          disableElevation
          onClick={onClick}
          disabled={!getCookiebyName("id_token") || banner === "true"}
        >
          {label}
        </Button>
      </Grid>
    );
  };

  const manageSaved = (title: string, isNotAuthenticated: boolean) => {
    return (
      <Typography
        variant="h2"
        color="primary"
        style={{
          opacity: isNotAuthenticated ? 0.6 : 1,
        }}
      >
        {title}
        <Divider
          className={classes.divider}
          style={{ opacity: isNotAuthenticated ? 0.6 : 1 }}
        />
      </Typography>
    );
  };

  const renderContent = () => {
    if (!getCookiebyName("id_token")) {
      return null;
    }

    return (
      <Grid container spacing={4}>
        {activeTab === "Custom Financial Reports" && (
          <Grid item md={6}>
            {manageSaved(
              "Create New Custom Financial Reports",
              !getCookiebyName("id_token"),
            )}
            {button("Define a Customized Report", classes.button, () =>
              history.push({
                pathname: `${AppUrl("PeerGroup")}/definereport`,
                state: {
                  url: AppUrl("PeerGroup") + window.location.search,
                },
              }),
            )}
          </Grid>
        )}

        {activeTab === "Custom Financial Reports" && (
          <Grid item md={6}>
            {manageSaved(
              "Manage Your Saved Customized Reports",
              !getCookiebyName("id_token"),
            )}
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              className={classes.margin}
            >
              {savedReports.length === 0 && !displayReportLoading ? (
                <Typography variant="body2" color="primary">
                  {!getCookiebyName("id_token")
                    ? "You must be signed in to create or see saved custom content."
                    : "You currently have no saved Custom Reports. Build one today!"}
                </Typography>
              ) : banner === "true" ? (
                <Typography variant="body2" color="primary">
                  Custom content is momentarily unavailable. Please try again
                  later.
                </Typography>
              ) : (
                SavedReportsList()
              )}
            </Grid>
          </Grid>
        )}

        {activeTab === "Custom Peer Groups" && (
          <Grid item md={6}>
            {manageSaved(
              "Create New Custom Peer Groups",
              !getCookiebyName("id_token"),
            )}
            {button("Build a Custom Peer Group", classes.button, () =>
              history.push({
                pathname: `${AppUrl("PeerGroup")}/definegroup`,
                state: {
                  url: AppUrl("PeerGroup") + window.location.search,
                },
              }),
            )}
          </Grid>
        )}

        {activeTab === "Custom Peer Groups" && (
          <Grid item md={6}>
            {manageSaved(
              "Manage Your Saved Custom Peer Groups",
              !getCookiebyName("id_token"),
            )}
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              className={classes.margin}
            >
              {savedGroups.length === 0 ? (
                <Typography variant="body2" color="primary">
                  {"You currently have no saved Peer Groups. Build one today!"}
                </Typography>
              ) : banner === "true" ? (
                <Typography variant="body2" color="primary">
                  Custom content is momentarily unavailable. Please try again
                  later.
                </Typography>
              ) : (
                SavedPeerGroupsList()
              )}
              {savedGroups.length !== 0 && (
                <Typography
                  variant="body2"
                  color="primary"
                  className={classes.footnote}
                >
                  <span style={{ fontWeight: "bold" }}>Note:</span> Custom Peer
                  groups do not include inactive institutions or institutions
                  that have dropped insurance for any given reporting period. As
                  a result, they can change over time, as institutions come and
                  go.
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
        {activeTab === "Custom SOD Downloads" && (
          <Grid item md={6}>
            {manageSaved(
              "Create New Custom SOD Download",
              !getCookiebyName("id_token"),
            )}
            {button("Build a Custom SOD Download", classes.button, () =>
              history.push({
                history: "peergroup",
                pathname: `${AppUrl("SOD")}/customDownload`,
                state: {
                  url: AppUrl("SOD") + window.location.search,
                },
              }),
            )}
          </Grid>
        )}

        {activeTab === "Custom SOD Downloads" && (
          <Grid item md={6}>
            {manageSaved(
              "Manage Your Saved SOD Downloads",
              !getCookiebyName("id_token"),
            )}
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              className={classes.margin}
            >
              {savedSODDownloadConfigs.length === 0 ? (
                <Typography variant="body2" color="primary">
                  {
                    "You currently have no saved SOD Download configurations. Build one today!"
                  }
                </Typography>
              ) : banner === "true" ? (
                <Typography variant="body2" color="primary">
                  Custom content is momentarily unavailable. Please try again
                  later.
                </Typography>
              ) : (
                SavedSODDownloadConfigs()
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  const introParagraph = (
    <>
      {appHeaderTitle("manageSavedPeerGroups")}
      <Typography variant="body2" color="primary" style={{ width: 926 }}>
        Maximize your analysis time by creating custom configurations of the
        reports you run most often. You can customize and manage financial
        reports, peer groups, and SOD download sets to focus on the data that is
        most important to you. Customize a wide array of criteria including:
        specific data sets, peer groups (either specific institutions sets or
        those defined by financial parameters, regional locations, business
        specialties), and more.
      </Typography>
      <Typography variant="body2" color="primary" style={{ width: 926 }}>
        Once saved, you can access and reuse your custom content again and
        again, saving you the time of not having to redefine them. If you need
        assistance, please see the{" "}
        <RouterLink to={"/help?helpTopic=tutorials"}>
          Help section for tutorials
        </RouterLink>{" "}
        to get you started.
      </Typography>
      <Typography variant="body2" color="primary" id="peer-group-feedback-link">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=PeerGroupManage"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
      {!getCookiebyName("id_token") && (
        <>
          <Typography variant="h3" style={{ paddingBottom: 0 }}>
            You must use LogIn.gov to create a personal account in order to
            access the functionality of this page.
          </Typography>
          <Typography variant="body2" color="primary" style={{ width: 926 }}>
            We use LogIn.gov as a safe and respected method of confirming user
            identity. It is a service of the GSA and is FREE to use. It offers
            single sign-on access to this and a host of other government
            websites. You can use your existing LogIn.gov account to sign-in, or
            you can quickly create one today.
          </Typography>
        </>
      )}
    </>
  );
  const breadCrumbTitle = "Manage Your Custom Content";
  return (
    <>
      {deleteDialog}
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle={breadCrumbTitle} />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        login
        introText={introParagraph}
        backButton={
          <BackButton
            app={
              backButtonState === "SOD"
                ? "SOD"
                : backButtonState === "FR"
                ? "FinancialReporting"
                : "PeerGroup"
            }
            buttonLabel={
              backButtonState === "SOD"
                ? "Back to SOD"
                : "Back to Financial Reporting"
            }
            variant="dark"
          />
        }
      >
        <br />
        {getCookiebyName("id_token") && (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={false}
                sx={{ borderBottom: 1, borderColor: "divider" }}
                aria-label="tabs"
              >
                {[
                  "Custom Financial Reports",
                  "Custom Peer Groups",
                  "Custom SOD Downloads",
                  // "Custom Charting",
                ].map((tab, index) => (
                  <Tab
                    label={tab}
                    id={tab}
                    key={tab}
                    onClick={() => {
                      setActiveTab(tab);
                      updateTab(tab);
                    }}
                    sx={{
                      "marginRight": "15px",
                      "backgroundColor":
                        activeTab === tab ? "#1A4480" : "#f0f0f0",
                      "color": activeTab === tab ? "#ffffff" : "#1A4480",
                      "&:hover": {
                        backgroundColor: activeTab === tab ? "" : "#D9E8F6",
                        color: activeTab === tab ? "#ffffff" : "#1A4480",
                      },
                      "textTransform": "none",
                      "fontFamily": "Source Sans Pro",
                      "fontSize": "16px",
                      "fontWeight": "bold",
                      "width": "333px",
                      "height": "35px",
                    }}
                  />
                ))}
              </Tabs>
              <Box
                sx={{
                  paddingTop: 5,
                }}
              >
                {renderContent()}
              </Box>
            </Box>
          </Box>
        )}
        <ModalDialog
          title={currentGroup.title}
          display={openGroupModal}
          setDisplay={setOpenGroupModal}
          maxWidth="lg"
        >
          <PeerGroupCriteriaTable
            includeReportPeriod
            currentGroup={currentGroup}
            appState={appState}
          />
        </ModalDialog>
        <ModalDialog
          title={currentReport.title}
          display={openReportModal}
          setDisplay={setOpenReportModal}
          maxWidth="lg"
        >
          <MultiHeaderTable
            tableBody={reportTableBody}
            tableHeaders={reportHeaders}
          />
        </ModalDialog>
        <ModalDialog
          title={currentSODDownload.title}
          display={openSODDownloadModal}
          setDisplay={setOpenSODDownloadModal}
          selectedLocations={currentSODDownload.locations}
          maxWidth="lg"
        >
          <MultiHeaderTable
            tableBody={sodDownloadTableBody}
            tableHeaders={sodDownloadConfigHeader}
          />
        </ModalDialog>
      </AppBody>
    </>
  );
}
